import { useState, useEffect } from "react";
import { Flame, Trophy, Crown, Music, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const rankStyles = [
  { bg: "from-amber-500/20 to-amber-900/10", border: "border-amber-500/40", badge: "bg-amber-500", icon: Crown, label: "1st" },
  { bg: "from-slate-300/15 to-slate-600/5", border: "border-slate-400/30", badge: "bg-slate-400", icon: Trophy, label: "2nd" },
  { bg: "from-orange-700/15 to-orange-900/5", border: "border-orange-600/30", badge: "bg-orange-600", icon: Trophy, label: "3rd" },
];

const ArtistCard = ({ artist, rank, isTop }) => {
  const style = rankStyles[rank - 1] || rankStyles[2];
  const Icon = style.icon;

  return (
    <div
      data-testid={`leaderboard-rank-${rank}`}
      className={`relative bg-gradient-to-br ${style.bg} border ${style.border} rounded-2xl overflow-hidden transition-all hover:scale-[1.02] ${
        isTop ? "p-5 sm:p-6" : "p-4"
      }`}
    >
      {/* Rank badge */}
      <div className={`absolute top-3 right-3 ${style.badge} w-8 h-8 rounded-full flex items-center justify-center shadow-lg`}>
        <span className="text-black font-bold text-sm">{rank}</span>
      </div>

      <div className={`flex ${isTop ? "flex-col sm:flex-row" : "flex-row"} items-center gap-4`}>
        {/* Artist art */}
        <div className={`${isTop ? "w-20 h-20 sm:w-24 sm:h-24" : "w-14 h-14"} rounded-xl overflow-hidden bg-white/5 flex-shrink-0`}>
          {artist.artwork_url ? (
            <img src={artist.artwork_url} alt={artist.artist} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className={`${isTop ? "w-8 h-8" : "w-6 h-6"} text-white/20`} />
            </div>
          )}
        </div>

        {/* Info */}
        <div className={`${isTop ? "text-center sm:text-left" : "text-left"} flex-1 min-w-0`}>
          <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
            <Icon className={`${isTop ? "w-4 h-4" : "w-3.5 h-3.5"} text-amber-400`} />
            <span className="text-white/40 text-xs font-medium uppercase tracking-wider">{style.label} Place</span>
          </div>
          <h3 className={`text-white font-bold truncate ${isTop ? "text-lg sm:text-xl" : "text-base"}`}>
            {artist.artist}
          </h3>
          {artist.genre && (
            <p className="text-white/30 text-xs mt-0.5">{artist.genre}</p>
          )}
          <div className={`flex items-center gap-3 mt-2 ${isTop ? "justify-center sm:justify-start" : ""}`}>
            <span className="text-green-400 font-bold text-sm">{artist.spins} spins</span>
            <span className="text-white/20">|</span>
            <span className="text-white/40 text-xs">{artist.tracks} track{artist.tracks !== 1 ? "s" : ""}</span>
            <span className="text-white/20">|</span>
            <span className="text-white/40 text-xs">{artist.total_listeners} listeners</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MonthlyLeaderboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/leaderboard/monthly`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const hasData = data?.leaderboard?.length > 0;
  const monthName = data?.month || new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <section className="mb-12" data-testid="monthly-leaderboard">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Flame className="w-6 h-6 text-orange-400" />
        <h2 className="text-white text-xl sm:text-2xl font-bold tracking-tight">
          Top Artist Of The Month
        </h2>
      </div>
      <p className="text-white/40 text-sm mb-1">
        {monthName}
      </p>
      <p className="text-white/30 text-xs mb-6">
        The artist with the most spins this month wins a featured placement + exclusive promotion.
      </p>

      {hasData ? (
        <>
          {/* Leaderboard */}
          <div className="space-y-3">
            {/* #1 — Spotlight */}
            <ArtistCard artist={data.leaderboard[0]} rank={1} isTop={true} />

            {/* #2 & #3 — Runner-ups */}
            {data.leaderboard.length > 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.leaderboard.slice(1).map((a) => (
                  <ArtistCard key={a.rank} artist={a} rank={a.rank} isTop={false} />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="border border-white/5 rounded-2xl p-8 text-center bg-white/[0.02]" data-testid="leaderboard-empty">
          <Music className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm font-medium mb-1">No spins tracked yet this month</p>
          <p className="text-white/15 text-xs">Submit your tracks to compete — spins are tracked automatically from live radio play.</p>
        </div>
      )}

      {/* CTA */}
      <div className="mt-4 flex items-center gap-2 text-white/25 text-xs">
        <TrendingUp className="w-3.5 h-3.5" />
        <span>Submit your tracks to compete — spins are tracked automatically from live radio play.</span>
      </div>
    </section>
  );
};
